import type { Approval, BuildingFamily, CompPick, FeasibilityRow, LimitingFactor } from '../types/feasibility';

/** FZ - the words and formats every feasibility screen uses. Plain hyphens only. */

export const APPROVAL_LABEL: Record<Approval, string> = {
  by_right: 'Allowed outright', permit: 'Needs a permit', unclear: 'Unclear - ask Planning',
  not_allowed_now: 'Not allowed right now', not_allowed: 'Not allowed', no_rules: 'Not researched yet',
  no_zone: 'Zone not looked up yet',
};

export const APPROVAL_TONE: Record<Approval, string> = {
  by_right: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  permit: 'bg-amber-50 text-amber-800 ring-amber-200',
  unclear: 'bg-sky-50 text-sky-800 ring-sky-200',
  not_allowed_now: 'bg-orange-50 text-orange-800 ring-orange-200',
  not_allowed: 'bg-gray-100 text-gray-600 ring-gray-200',
  no_rules: 'bg-gray-50 text-gray-500 ring-gray-200',
  no_zone: 'bg-gray-50 text-gray-500 ring-gray-200',
};

export const PERMIT_LABEL: Record<string, string> = {
  cup: 'a conditional use permit', minor_use_permit: 'a minor use permit',
  administrative_use_permit: 'an administrative use permit', site_plan_review: 'site plan review',
  plot_plan: 'a plot plan', development_permit: 'a development permit', other: 'a permit',
};

export const FACTOR_LABEL: Record<LimitingFactor, string> = {
  far: 'the floor-area ratio', lot_coverage: 'the lot coverage limit',
  practical_coverage: 'practical site coverage (truck courts, trailer stalls, setbacks)',
  parking: 'the parking requirement', landscaping: 'the landscaping minimum', min_lot: 'the minimum lot size',
};

export const FAMILY_LABEL: Record<BuildingFamily, string> = {
  industrial_building: 'Industrial building', retail_building: 'Retail building',
};

/**
 * The nine FZ uses (the backend's `NEW_USE_CASES`). Their answers are approved only by the two-read rule, never by a
 * person's city sign-off - the server refuses that (the 141st's look), and the Zoning Rules button counts without them.
 */
export const FZ_USE_CASES: readonly string[] = [
  'warehouse_distribution', 'warehouse_large', 'manufacturing_light', 'manufacturing_heavy',
  'retail_store', 'restaurant', 'drive_through', 'gas_station', 'car_wash',
];

export const USE_LABEL: Record<string, string> = {
  warehouse_distribution: 'warehouse / distribution', warehouse_large: 'large warehouse',
  manufacturing_light: 'light manufacturing', manufacturing_heavy: 'heavy manufacturing',
  retail_store: 'retail store', restaurant: 'restaurant', drive_through: 'drive-through restaurant',
  gas_station: 'gas station', car_wash: 'car wash',
};

export const STANDARD_LABEL: Record<string, string> = {
  far_max: 'Floor-area ratio', lot_coverage_max: 'Lot coverage', height_max_ft: 'Height', stories_max: 'Stories',
  min_lot_sf: 'Minimum lot', setback_front_ft: 'Front setback', setback_side_ft: 'Side setback',
  setback_rear_ft: 'Rear setback', parking_per_1000_sf: 'Parking per 1,000 sq ft', landscape_min: 'Landscaping minimum',
  warehouse_large_threshold_sf: 'Large-warehouse cutoff', lot_area: 'the lot area',
  practical_coverage_industrial: 'the practical coverage default', stall_sf: 'the parking stall default',
};

export const ASSUMPTION_LABEL: Record<string, string> = {
  hard_cost_psf_industrial: 'Construction cost per sq ft, industrial building',
  hard_cost_psf_retail: 'Construction cost per sq ft, retail building',
  site_work_psf_land: 'Site work per sq ft of land',
  fees_psf_industrial: 'City and regional fees per sq ft, industrial',
  fees_psf_retail: 'City and regional fees per sq ft, retail',
  soft_cost_share: 'Soft costs as a share of construction',
  profit_share: "Developer's profit as a share of the finished value",
};

export const usd = (n: number | null | undefined): string =>
  n === null || n === undefined ? '-' : `${n < 0 ? '-' : ''}$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;
export const sqft = (n: number | null | undefined): string =>
  n === null || n === undefined ? '-' : `${Math.round(n).toLocaleString('en-US')} sq ft`;
export const acres = (sf: number | null | undefined): string =>
  sf === null || sf === undefined ? '-' : `${(sf / 43_560).toFixed(2)} ac`;
export const pct = (share: number | null | undefined): string =>
  share === null || share === undefined ? '-' : `${Math.round(share * 1000) / 10}%`;

/** Spec s.8 point 3 (the 141st's look): below zero is said plainly - and the number stays beside the words. */
export const DOES_NOT_PENCIL = "a building does not pencil here at today's prices";
export const residualText = (row: Pick<FeasibilityRow, 'residual_value' | 'residual_note'>): string => {
  if (row.residual_value === null) return row.residual_note ?? '-';
  return row.residual_value < 0 ? `${usd(row.residual_value)} - ${DOES_NOT_PENCIL}` : usd(row.residual_value);
};

/** The lot every size rests on, and where it came from - the page header can show a different record's acreage. */
export const lotSentence = (row: Pick<FeasibilityRow, 'land_sf' | 'land_sf_source'>): string | null => {
  if (row.land_sf === null) return null;
  const from = row.land_sf_source === 'parcels' ? 'the county parcel map' : 'the property record';
  return `Lot ${sqft(row.land_sf)} (${acres(row.land_sf)}) - from ${from}`;
};

const useWords = (row: FeasibilityRow) => USE_LABEL[row.approval_use ?? ''] ?? 'this use';
const isPlay = (row: Pick<FeasibilityRow, 'approval'>) => row.approval === 'by_right' || row.approval === 'permit';
/**
 * FZ-FIX I10: a retail answer earned by a restaurant or drive-through is a PAD. The engine's rule (approval.ts): a shop
 * earns the retail building, so a retail play whose use is a restaurant or drive-through is one only when no shop is.
 */
export const isPad = (row: Pick<FeasibilityRow, 'use_family' | 'approval' | 'approval_use'>): boolean =>
  row.use_family === 'retail_building' && isPlay(row) && (row.approval_use === 'restaurant' || row.approval_use === 'drive_through');

export const approvalSentence = (row: FeasibilityRow): string => {
  switch (row.approval) {
    case 'by_right': return isPad(row) ? `Allowed outright as a ${useWords(row)} only` : `Allowed outright as ${useWords(row)}`;
    case 'permit': {
      const permit = PERMIT_LABEL[row.permit_type ?? 'cup'] ?? 'a permit';
      return isPad(row) ? `Needs ${permit} for a ${useWords(row)} only` : `Needs ${permit} for ${useWords(row)}`;
    }
    case 'not_allowed_now': return `Not allowed right now${row.approval_until ? `, until ${row.approval_until}` : ''}`;
    default: return APPROVAL_LABEL[row.approval];
  }
};

export const sizeSentence = (row: FeasibilityRow): string => {
  // FZ-FIX I5 + I10 (the 142nd): no size is stored where the building is not allowed, not answered, or a pad - say why.
  if (isPad(row)) return 'No building size - a restaurant pad, not a retail building (pads are not valued yet)';
  if (!isPlay(row)) return 'No building size - one is worked out only where the building is allowed';
  if (row.too_small) return 'Too small to build on alone (under the minimum lot)';
  if (row.max_building_sf !== null && row.limiting_factor) {
    return `Up to ${sqft(row.max_building_sf)} - ${FACTOR_LABEL[row.limiting_factor]} decides it`;
  }
  if (row.missing.length) return `Size unknown - missing ${row.missing.map((m) => STANDARD_LABEL[m] ?? m).join(', ')}`;
  return 'Size not worked out';
};

export const compsSentence = (c: CompPick | null, unit: 'sq ft' | 'acre'): string | null => {
  if (!c || c.median === null) return null;
  const where = c.tier === 'jurisdiction' ? 'same city' : 'within the search radius';
  return `${c.count} sales, ${where}, median ${usd(c.median)} per ${unit}${c.newerOnly ? ', newer buildings only' : ''}`;
};

/**
 * FZ-FIX I10 (the 142nd; his call): the line on the phone names the use that earned the answer - "Allowed outright
 * (restaurant only)" - so a pad never reads like a retail building. Not for an answer that is not a play.
 */
export const phoneApproval = (row: FeasibilityRow): string => {
  const label = APPROVAL_LABEL[row.approval];
  if (!isPlay(row) || !row.approval_use) return label;
  return `${label} (${USE_LABEL[row.approval_use] ?? row.approval_use}${isPad(row) ? ' only' : ''})`;
};
